if (!customElements.get('collapsible-section-group')) {
  class CollapsibleSectionGroup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.toggle = this.querySelector('.s-collapsible-section-group__toggle');
      if (!this.toggle) {
        requestAnimationFrame(() => this.connectedCallback());
        return;
      }

      this.initialized = true;
      this.selfId = this.dataset.selfId || '';
      this.sectionIds = (this.dataset.sectionIds || '').split(',').map(s => s.trim()).filter(Boolean);
      this.isOpen = this.dataset.open === 'true';

      this.targetSections = [];
      this.resolveTargetSections();

      this.toggle.addEventListener('click', () => this.handleToggle());

      // Design mode: show sections when this section is selected in Theme Editor.
      document.addEventListener('shopify:section:select', (evt) => {
        if (evt.detail.sectionId === this.selfId) this.open();
      });

      document.addEventListener('shopify:section:deselect', (evt) => {
        if (evt.detail.sectionId === this.selfId && !this.isOpen) this.close();
      });
    }

    handleToggle() {
      this.isOpen ? this.close() : this.open();
    }

    resolveTargetSections() {
      // Use getElementById to avoid CSS selector parsing issues with IDs containing '--'
      this.targetSections = this.sectionIds
        .map(id => document.getElementById(`shopify-section-${id}`))
        .filter(Boolean);
    }

    animateSection(section, isOpening) {
      if (section._csgAnimation) section._csgAnimation.cancel();

      if (isOpening) {
        section.style.display = 'block';
        section.style.overflow = 'hidden';
        section.style.height = '0px';
        section.style.opacity = '0';
      } else {
        section.style.display = 'block';
        section.style.overflow = 'hidden';
        section.style.height = `${section.offsetHeight}px`;
      }

      const targetHeight = isOpening ? section.scrollHeight : 0;
      const animation = section.animate(
        [
          { height: isOpening ? '0px' : `${section.offsetHeight}px`, opacity: isOpening ? 0 : 1 },
          { height: `${targetHeight}px`, opacity: isOpening ? 1 : 0 }
        ],
        {
          duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 320,
          easing: 'cubic-bezier(0.2, 0.6, 0.4, 1)'
        }
      );

      section._csgAnimation = animation;
      animation.onfinish = () => {
        section._csgAnimation = null;
        section.style.height = '';
        section.style.overflow = '';
        section.style.opacity = '';
        if (!isOpening) section.style.display = 'none';
      };
    }

    open() {
      this.resolveTargetSections();
      this.isOpen = true;
      this.toggle.setAttribute('aria-expanded', 'true');
      // Remove the synchronous init style so no CSS rule fights the inline display
      const initStyle = document.getElementById(`csg-init-${this.selfId}`);
      if (initStyle) initStyle.remove();
      this.targetSections.forEach(el => this.animateSection(el, true));
      const last = this.targetSections[this.targetSections.length - 1];
      if (last) last.classList.add('csg-last-expanded');

      // Accordion behavior: only when this toggle is part of an inline row, opening it closes
      // the other inline toggles (stacked/non-inline toggles are unaffected).
      if (window.matchMedia('(min-width: 769px)').matches && this.closest('.s-collapsible-section-group--inline')) {
        document.querySelectorAll('.s-collapsible-section-group--inline collapsible-section-group').forEach((el) => {
          if (el !== this && el.isOpen) el.close();
        });
      }
    }

    close() {
      this.resolveTargetSections();
      this.isOpen = false;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.targetSections.forEach(el => {
        this.animateSection(el, false);
        el.classList.remove('csg-last-expanded');
      });
    }
  }

  customElements.define('collapsible-section-group', CollapsibleSectionGroup);
}

const inlineSectionPositions = new Map();

function groupInlineCollapsibleSections() {
  const wrappers = Array.from(document.querySelectorAll('.shopify-section')).filter((el) =>
    el.querySelector(':scope > .s-collapsible-section-group--inline')
  );
  if (wrappers.length < 2) return;

  if (!window.matchMedia('(min-width: 769px)').matches) {
    restoreInlineSections(wrappers);
    return;
  }

  let row = wrappers[0].closest('.s-collapsible-section-group-row');
  if (!row) {
    row = document.createElement('div');
    row.className = 's-collapsible-section-group-row';
    wrappers[0].before(row);
  }
  wrappers.forEach((el) => {
    if (!inlineSectionPositions.has(el)) {
      inlineSectionPositions.set(el, { parent: el.parentElement, nextSibling: el.nextElementSibling });
    }
    row.appendChild(el);
  });

  document.querySelectorAll('.s-collapsible-section-group-row').forEach((candidate) => {
    if (candidate !== row && !candidate.querySelector('.shopify-section')) candidate.remove();
  });
}

function restoreInlineSections(wrappers) {
  wrappers.forEach((wrapper) => {
    const position = inlineSectionPositions.get(wrapper);
    if (!position || !position.parent.isConnected) return;
    position.parent.insertBefore(wrapper, position.nextSibling && position.nextSibling.parentElement === position.parent
      ? position.nextSibling
      : null);
  });

  document.querySelectorAll('.s-collapsible-section-group-row').forEach((row) => {
    if (!row.querySelector('.shopify-section')) row.remove();
  });
}

document.addEventListener('DOMContentLoaded', groupInlineCollapsibleSections);
document.addEventListener('shopify:section:load', groupInlineCollapsibleSections);
document.addEventListener('shopify:section:unload', groupInlineCollapsibleSections);
document.addEventListener('shopify:section:reorder', groupInlineCollapsibleSections);
window.addEventListener('resize', groupInlineCollapsibleSections);
